import { Numeric, Objects } from "../util";
import { CalculationResult, EmployerCalculationDTO, TotalCalculationDTO, WorkerCalculationDTO } from "../model";

/** Extension methods for CalculationResult */
export class CalculationResultLogic {

  /**
   * Adds Calculation Results together and returns a new CalculationResult
   * @param calculationResults CalculationResults to add.
   * @returns A new CalculationResult.
   */
  public static add(calculationResults: CalculationResult[] ): CalculationResult {
    if (calculationResults == null || calculationResults.length === 0) {
      return this.addResults(null, null);
    }
    return calculationResults.reduce( (sum, item) => this.addResults(sum, item), this.safeObj(null));
  }


  /**
   * Calculates the difference between two results and returns a new CalculationResult
   * @param original - The original result.
   * @param modified - The modified result.
   * @returns The difference between the result sets.
   */
   public static diff(original: CalculationResult, modified: CalculationResult): CalculationResult {
    return this.addResults(modified, original, true);
  }

  private static addResults(c1: CalculationResult, c2: CalculationResult, subtract = false): CalculationResult {
    c1 = this.safeObj(c1);
    c2 = this.safeObj(c2);
    return {
      totals: this.addTotals(c1.totals, c2.totals, subtract),
      workerCalc: this.addWorkerCalc(c1.workerCalc, c2.workerCalc, subtract),
      employerCalc: this.addEmployerCalc(c1.employerCalc, c2.employerCalc, subtract),
      rows: subtract ? this.safeArr(null) : this.safeArr(c1.rows).concat(this.safeArr(c2.rows)),
      responsibilities: this.safeObj(c1.responsibilities),
      irRows: subtract ? this.safeArr(null) : this.safeArr(c1.irRows).concat(this.safeArr(c2.irRows)),
    };
  }

  private static addTotals(c1: TotalCalculationDTO, c2: TotalCalculationDTO, subtract = false): TotalCalculationDTO {
    c1 = this.safeObj(c1);
    c2 = this.safeObj(c2);
    return  {
      pension: this.safeNum(c1.pension) + this.safeNum(c2.pension, subtract),
      total: this.safeNum(c1.total) + this.safeNum(c2.total, subtract),
      totalBaseSalary: this.safeNum(c1.totalBaseSalary) + this.safeNum(c2.totalBaseSalary, subtract),
      totalExpenses: this.safeNum(c1.totalExpenses) + this.safeNum(c2.totalExpenses, subtract),
      totalGrossSalary: this.safeNum(c1.totalGrossSalary) + this.safeNum(c2.totalGrossSalary, subtract),
      totalSocialSecurityBase: this.safeNum(c1.totalSocialSecurityBase) + this.safeNum(c2.totalSocialSecurityBase, subtract),
      totalTaxable: this.safeNum(c1.totalTaxable) + this.safeNum(c2.totalTaxable, subtract),
      unemployment: this.safeNum(c1.unemployment) + this.safeNum(c2.unemployment, subtract),
      totalAccidentInsuranceBase: this.safeNum(c1.totalAccidentInsuranceBase) + this.safeNum(c2.totalAccidentInsuranceBase, subtract),
      totalHealthInsuranceBase: this.safeNum(c1.totalHealthInsuranceBase) + this.safeNum(c2.totalHealthInsuranceBase, subtract),
      totalPayable: this.safeNum(c1.totalPayable) + this.safeNum(c2.totalPayable, subtract),
      totalPensionInsuranceBase: this.safeNum(c1.totalPensionInsuranceBase) + this.safeNum(c2.totalPensionInsuranceBase, subtract),
      totalUnemploymentInsuranceBase: this.safeNum(c1.totalUnemploymentInsuranceBase) + this.safeNum(c2.totalUnemploymentInsuranceBase, subtract),
    };
  }

  private static addWorkerCalc(c1: WorkerCalculationDTO, c2: WorkerCalculationDTO, subtract = false): WorkerCalculationDTO {
    c1 = this.safeObj(c1);
    c2 = this.safeObj(c2);
    return {
      benefits : this.safeNum(c1.benefits) + this.safeNum(c2.benefits, subtract),
      deductions : this.safeNum(c1.deductions) + this.safeNum(c2.deductions, subtract),
      fullTax : this.safeNum(c1.fullTax) + this.safeNum(c2.fullTax, subtract),
      fullOtherDeductions : this.safeNum(c1.fullOtherDeductions) + this.safeNum(c2.fullOtherDeductions, subtract),
      fullPension : this.safeNum(c1.fullPension) + this.safeNum(c2.fullPension, subtract),
      fullPrepaidExpenses : this.safeNum(c1.fullPrepaidExpenses) + this.safeNum(c2.fullPrepaidExpenses, subtract),
      fullSalaryAdvance : this.safeNum(c1.fullSalaryAdvance) + this.safeNum(c2.fullSalaryAdvance, subtract),
      fullUnemploymentInsurance : this.safeNum(c1.fullUnemploymentInsurance) + this.safeNum(c2.fullUnemploymentInsurance, subtract),
      fullUnionPayment : this.safeNum(c1.fullUnionPayment) + this.safeNum(c2.fullUnionPayment, subtract),
      otherDeductions : this.safeNum(c1.otherDeductions) + this.safeNum(c2.otherDeductions, subtract),
      pension : this.safeNum(c1.pension) + this.safeNum(c2.pension, subtract),
      prepaidExpenses : this.safeNum(c1.prepaidExpenses) + this.safeNum(c2.prepaidExpenses, subtract),
      foreclosure : this.safeNum(c1.foreclosure) + this.safeNum(c2.foreclosure, subtract),
      salaryAdvance : this.safeNum(c1.salaryAdvance) + this.safeNum(c2.salaryAdvance, subtract),
      salaryAfterTax : this.safeNum(c1.salaryAfterTax) + this.safeNum(c2.salaryAfterTax, subtract),
      salaryAfterTaxAndForeclosure : this.safeNum(c1.salaryAfterTaxAndForeclosure) + this.safeNum(c2.salaryAfterTaxAndForeclosure, subtract),
      salaryPayment : this.safeNum(c1.salaryPayment) + this.safeNum(c2.salaryPayment, subtract),
      tax : this.safeNum(c1.tax) + this.safeNum(c2.tax, subtract),
      totalWorkerPayment : this.safeNum(c1.totalWorkerPayment) + this.safeNum(c2.totalWorkerPayment, subtract),
      unemploymentInsurance : this.safeNum(c1.unemploymentInsurance) + this.safeNum(c2.unemploymentInsurance, subtract),
      unionPayment : this.safeNum(c1.unionPayment) + this.safeNum(c2.unionPayment, subtract),
      workerSideCosts : this.safeNum(c1.workerSideCosts) + this.safeNum(c2.workerSideCosts, subtract),
      totalWorkerPaymentLegacy : this.safeNum(c1.totalWorkerPaymentLegacy) + this.safeNum(c2.totalWorkerPaymentLegacy, subtract),
    };
  }

  private static addEmployerCalc(c1: EmployerCalculationDTO, c2: EmployerCalculationDTO, subtract = false): EmployerCalculationDTO {
    c1 = this.safeObj(c1);
    c2 = this.safeObj(c2);
    const result = {
      allSideCosts : this.safeNum(c1.allSideCosts) + this.safeNum(c2.allSideCosts, subtract),
      deductionPensionSelfPayment : this.safeNum(c1.deductionPensionSelfPayment) + this.safeNum(c2.deductionPensionSelfPayment, subtract),
      deductionUnemploymentSelfPayment : this.safeNum(c1.deductionUnemploymentSelfPayment) + this.safeNum(c2.deductionUnemploymentSelfPayment, subtract),
      deductionSalaryAdvance : this.safeNum(c1.deductionSalaryAdvance) + this.safeNum(c2.deductionSalaryAdvance, subtract),
      deductionForeclosure : this.safeNum(c1.deductionForeclosure) + this.safeNum(c2.deductionForeclosure, subtract),
      foreclosureByPalkkaus : this.safeNum(c1.foreclosureByPalkkaus) + this.safeNum(c2.foreclosureByPalkkaus, subtract),
      deductionUnionPayment : this.safeNum(c1.deductionUnionPayment) + this.safeNum(c2.deductionUnionPayment, subtract),
      deductionOtherDeductions : this.safeNum(c1.deductionOtherDeductions) + this.safeNum(c2.deductionOtherDeductions, subtract),
      deductionTaxAndSocialSecuritySelfPayment: this.safeNum(c1.deductionTaxAndSocialSecuritySelfPayment) + this.safeNum(c2.deductionTaxAndSocialSecuritySelfPayment, subtract),
      deductionWorkerSelfPayment: this.safeNum(c1.deductionWorkerSelfPayment) + this.safeNum(c2.deductionWorkerSelfPayment, subtract),
      finalCost : this.safeNum(c1.finalCost) + this.safeNum(c2.finalCost, subtract),
      householdDeduction : this.safeNum(c1.householdDeduction) + this.safeNum(c2.householdDeduction, subtract),
      mandatorySideCosts : this.safeNum(c1.mandatorySideCosts) + this.safeNum(c2.mandatorySideCosts, subtract),
      palkkaus : this.safeNum(c1.palkkaus) + this.safeNum(c2.palkkaus, subtract),
      service : this.safeNum(c1.service) + this.safeNum(c2.service, subtract),
      pension : this.safeNum(c1.pension) + this.safeNum(c2.pension, subtract),
      socialSecurity : this.safeNum(c1.socialSecurity) + this.safeNum(c2.socialSecurity, subtract),
      totalDeductions : this.safeNum(c1.totalDeductions) + this.safeNum(c2.totalDeductions, subtract),
      totalPayment : this.safeNum(c1.totalPayment) + this.safeNum(c2.totalPayment, subtract),
      totalSalaryCost : this.safeNum(c1.totalSalaryCost) + this.safeNum(c2.totalSalaryCost, subtract),
      unemployment : this.safeNum(c1.unemployment) + this.safeNum(c2.unemployment, subtract),
      totalPaymentLegacy : this.safeNum(c1.totalPaymentLegacy) + this.safeNum(c2.totalPaymentLegacy, subtract),
    };
    return result;
  }

  private static safeNum(value: number, subtract = false): number {
    const n = (value || 0);
    return subtract ? Numeric.round(n * -1.0) : n;
  }
  private static safeArr(value: any[]): any[] {
    return Objects.copy(value || []);
  }
  private static safeObj(value: any): any {
    return Objects.copy(value || {});
  }
}
